import { Coordinate } from "@nova/protobuf"
import { pointInPolygon } from "./calculate-geospatial"

const EPSILON = 1e-9

type Vertex = {
    point: Coordinate
    next: Vertex
    prev: Vertex
    intersect: boolean
    entry: boolean
    visited: boolean
    neighbor: Vertex | null
}

function makeVertex(point: Coordinate) {
    const vertex = {
        point,
        intersect: false,
        entry: false,
        visited: false,
        neighbor: null,
    } as Vertex
    vertex.next = vertex
    vertex.prev = vertex
    return vertex
}

function buildRing(points: Coordinate[]) {
    const vertices = points.map(makeVertex)
    vertices.forEach((vertex, i) => {
        vertex.next = vertices[(i + 1) % vertices.length]
        vertex.prev = vertices[(i - 1 + vertices.length) % vertices.length]
    })
    return vertices
}

function segmentIntersection(p1: Coordinate, p2: Coordinate, q1: Coordinate, q2: Coordinate) {
    const d1x = p2.longitude - p1.longitude
    const d1y = p2.latitude - p1.latitude
    const d2x = q2.longitude - q1.longitude
    const d2y = q2.latitude - q1.latitude
    const denominator = d1x * d2y - d1y * d2x
    if (Math.abs(denominator) < EPSILON) return undefined

    const dx = q1.longitude - p1.longitude
    const dy = q1.latitude - p1.latitude
    const t = (dx * d2y - dy * d2x) / denominator
    const u = (dx * d1y - dy * d1x) / denominator
    if (t <= EPSILON || t >= 1 - EPSILON || u <= EPSILON || u >= 1 - EPSILON) return undefined

    return { t, u, point: { longitude: p1.longitude + t * d1x, latitude: p1.latitude + t * d1y } }
}

function splice(ring: Vertex[], inserts: { alpha: number; vertex: Vertex }[][]) {
    for (let i = 0; i < ring.length; i++) {
        const group = inserts[i].sort((a, b) => a.alpha - b.alpha)
        if (!group.length) continue

        const after = ring[i].next
        let previous = ring[i]
        for (const { vertex } of group) {
            previous.next = vertex
            vertex.prev = previous
            previous = vertex
        }
        previous.next = after
        after.prev = previous
    }
}

function insertIntersections(subject: Vertex[], clip: Vertex[]) {
    const subjectInserts = subject.map(() => [] as { alpha: number; vertex: Vertex }[])
    const clipInserts = clip.map(() => [] as { alpha: number; vertex: Vertex }[])
    const subjectIntersections: Vertex[] = []

    for (let i = 0; i < subject.length; i++) {
        const p1 = subject[i].point
        const p2 = subject[(i + 1) % subject.length].point
        for (let j = 0; j < clip.length; j++) {
            const q1 = clip[j].point
            const q2 = clip[(j + 1) % clip.length].point
            const hit = segmentIntersection(p1, p2, q1, q2)
            if (!hit) continue

            const subjectVertex = makeVertex(hit.point)
            const clipVertex = makeVertex(hit.point)
            subjectVertex.intersect = true
            clipVertex.intersect = true
            subjectVertex.neighbor = clipVertex
            clipVertex.neighbor = subjectVertex

            subjectInserts[i].push({ alpha: hit.t, vertex: subjectVertex })
            clipInserts[j].push({ alpha: hit.u, vertex: clipVertex })
            subjectIntersections.push(subjectVertex)
        }
    }

    splice(subject, subjectInserts)
    splice(clip, clipInserts)
    return subjectIntersections
}

// entry marks where the boundary crosses from outside to inside the other polygon;
// trace() below walks forward from an entry vertex, backward from an exit vertex.
function markEntryExit(start: Vertex, otherRing: Coordinate[]) {
    let inside = pointInPolygon(start.point, otherRing)
    let node = start
    do {
        if (node.intersect) {
            inside = !inside
            node.entry = inside
        }
        node = node.next
    } while (node !== start)
}

// A well-formed trace visits each intersection at most twice; far beyond that it's stuck
// on near-duplicate points confusing entry/exit ordering -- bail instead of hanging.
function loopGuardLimit(intersectionCount: number) {
    return Math.max(1000, intersectionCount * 20)
}

function trace(subjectIntersections: Vertex[]) {
    const limit = loopGuardLimit(subjectIntersections.length)
    const loops: Coordinate[][] = []
    for (const start of subjectIntersections) {
        if (start.visited) continue

        const loop: Coordinate[] = [start.point]
        start.visited = true
        let current = start
        let steps = 0
        do {
            const forward = current.entry
            do {
                current = forward ? current.next : current.prev
                current.visited = true
                loop.push(current.point)
                if (++steps > limit) throw new PolygonDifferenceUnstableError()
            } while (!current.intersect)
            current = current.neighbor!
        } while (current !== start)

        loops.push(loop)
    }
    return loops
}

export class PolygonDifferenceUnstableError extends Error {
    constructor() {
        super("polygonDifference: trace did not terminate, input geometry is likely degenerate")
    }
}

function classifyNoIntersection(subjectPoints: Coordinate[], clipPoints: Coordinate[]) {
    if (pointInPolygon(subjectPoints[0], clipPoints)) return []
    if (pointInPolygon(clipPoints[0], subjectPoints)) return [subjectPoints, clipPoints]
    return [subjectPoints]
}

export function polygonDifference(subjectPoints: Coordinate[], clipPoints: Coordinate[]) {
    const subject = buildRing(subjectPoints.slice(0, -1))
    const clip = buildRing(clipPoints.slice(0, -1))

    const subjectIntersections = insertIntersections(subject, clip)
    if (!subjectIntersections.length) return classifyNoIntersection(subjectPoints, clipPoints)

    markEntryExit(subject[0], clipPoints)
    markEntryExit(clip[0], subjectPoints)
    // Flipping just subject's flags (leaving clip's as computed) turns the tracer below
    // from computing an intersection into computing subject-minus-clip.
    for (const vertex of subjectIntersections) vertex.entry = !vertex.entry

    return trace(subjectIntersections)
}
